import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Link2, Users } from "lucide-react";
import { initials, PageHeader } from "@/components/common";
import Pager from "@/components/Pager";

export default function RelasiWali() {
  const qc = useQueryClient();
  const { data: santri = [] } = useQuery({ queryKey: ["santri"], queryFn: async () => (await api.get("/santri")).data });

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ nomor_wa_wali: "", nama_wali: "" });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 30;

  const linkMut = useMutation({
    mutationFn: async () => api.patch(`/santri/${target.id}/wali`, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["santri"] }); toast.success("Relasi wali diperbarui"); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Gagal"),
  });

  const groups = {};
  const unlinked = [];
  santri.forEach((s) => {
    if (s.nomor_wa_wali) { (groups[s.nomor_wa_wali] = groups[s.nomor_wa_wali] || { nama: s.nama_wali, wa: s.nomor_wa_wali, anak: [] }).anak.push(s); }
    else unlinked.push(s);
  });

  const allGroups = Object.values(groups).filter((g) => !q || (g.nama || "").toLowerCase().includes(q.toLowerCase()) || g.wa.includes(q) || g.anak.some((a) => a.nama.toLowerCase().includes(q.toLowerCase()) || (a.no_induk_pondok || "").includes(q)));
  const pages = Math.max(1, Math.ceil(allGroups.length / PER_PAGE));
  const safePage = Math.min(page, pages);
  const pageGroups = allGroups.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);
  const openLink = (s) => { setTarget(s); setForm({ nomor_wa_wali: s.nomor_wa_wali || "", nama_wali: s.nama_wali || "" }); setOpen(true); };

  return (
    <div className="space-y-5">
      <PageHeader icon={Link2} title="Relasi Wali ↔ Santri" subtitle="Satu nomor WA wali dapat terhubung ke beberapa santri sekaligus; wali login dengan No. Induk salah satu anak dan melihat semua anaknya dalam satu dashboard. Format nomor: 08… atau +62…" />

      <div className="flex items-center gap-3 flex-wrap">
        <Input data-testid="relasi-search" placeholder="Cari wali / nomor WA / nama santri..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-full sm:w-72 rounded-xl bg-white/60 dark:bg-white/5 h-10" />
        <span className="text-xs text-slate-400">{allGroups.length} wali · {unlinked.length} santri belum ada WA</span>
      </div>
      <Pager page={safePage} pages={pages} total={allGroups.length} onChange={setPage} testId="relasi-pager" />
      <div className="grid gap-4 md:grid-cols-2">
        {pageGroups.map((g) => (
          <div key={g.wa} className="glass rounded-2xl p-5" data-testid={`wali-group-${g.wa}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Users className="h-4 w-4" /></div>
                <div><p className="font-semibold text-emerald-950 dark:text-emerald-50">{g.nama || "Wali"}</p><p className="text-xs text-slate-400">WA: {g.wa}</p></div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">{g.anak.length} Anak</Badge>
            </div>
            <div className="space-y-2">
              {g.anak.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7"><AvatarImage src={s.foto} /><AvatarFallback className="text-[10px] bg-emerald-100 text-emerald-700">{initials(s.nama)}</AvatarFallback></Avatar>
                    <span className="text-sm">{s.nama} <span className="text-slate-400">· {s.no_induk_pondok}</span></span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openLink(s)}>Ubah</Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {unlinked.length > 0 && (
        <div>
          <h4 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3">Belum Ada Nomor WA ({unlinked.length})</h4>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {unlinked.map((s) => (
              <div key={s.id} className="glass rounded-xl p-3 flex items-center justify-between" data-testid={`unlinked-${s.no_induk_pondok}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-8 w-8"><AvatarImage src={s.foto} /><AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">{initials(s.nama)}</AvatarFallback></Avatar>
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{s.nama}</p><p className="text-xs text-slate-400">{s.no_induk_pondok}</p></div>
                </div>
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-lg text-xs" data-testid={`admin-link-wali-santri-button-${s.no_induk_pondok}`} onClick={() => openLink(s)}><Link2 className="h-3.5 w-3.5" /> Hubungkan</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Hubungkan Wali — {target?.nama}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Nomor WA Wali</Label><Input data-testid="relasi-wa-input" placeholder="6281234567890" value={form.nomor_wa_wali} onChange={(e) => setForm({ ...form, nomor_wa_wali: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Nama Wali</Label><Input value={form.nama_wali} onChange={(e) => setForm({ ...form, nama_wali: e.target.value })} /></div>
            <p className="text-xs text-slate-400">Gunakan Nomor WA yang sama untuk menghubungkan beberapa anak ke satu wali.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-relasi-button" onClick={() => linkMut.mutate()} disabled={linkMut.isPending} className="bg-emerald-700 hover:bg-emerald-800">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
